import React, { useEffect, useState, useCallback } from "react";
import {
  getPendingFornitori,
  approveFornitore,
  rejectFornitore,
  adminListSupplierProfileRequests,
  adminApproveSupplierProfileRequest,
  adminRejectSupplierProfileRequest,
} from "./adminApi";
import "./AdminStyles.css";
import SupplierImageCropper from "../components/SupplierImageCropper";
import toast from "react-hot-toast";

export default function AdminApprovals() {
  const token = localStorage.getItem("token");
  const [cropTarget, setCropTarget] = useState(null);
  const [crop, setCrop] = useState(null);
  const [cropBusy, setCropBusy] = useState(false);
  const [cropError, setCropError] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);

  const [profileRequests, setProfileRequests] = useState([]);
  const [profileReqLoading, setProfileReqLoading] = useState(true);
  const [profileBusyId, setProfileBusyId] = useState(null);
  const [profileBusyAction, setProfileBusyAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProfileReqLoading(true);

    try {
      const [data, profileData] = await Promise.all([
        getPendingFornitori(token),
        adminListSupplierProfileRequests(token),
      ]);

      setItems(Array.isArray(data) ? data : []);
      setProfileRequests(Array.isArray(profileData) ? profileData : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setProfileReqLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id) => {
    const item = items.find(f => f.user_id === id);
    if (item?.cover_url) {
      setCropTarget({ id, source: item.cover_url, type: "registration" });
      setCrop(null); setCropError(""); return;
    }
    if (!window.confirm("Approvare questo fornitore?")) return;

    try {
      setBusyId(id);
      setBusyAction("approve");
      await approveFornitore(id, token);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Approvazione fallita");
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Rifiutare questa richiesta?")) return;

    try {
      setBusyId(id);
      setBusyAction("reject");
      await rejectFornitore(id, token);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleApproveProfileRequest = async (id) => {
    const item = profileRequests.find(r => r.id === id);
    if (item?.requested_cover_url) {
      setCropTarget({ id, source: item.requested_cover_url, type: "profile" });
      setCrop(null); setCropError(""); return;
    }
    if (!window.confirm("Approvare questa modifica profilo fornitore?")) return;

    try {
      setProfileBusyId(id);
      setProfileBusyAction("approve");
      await adminApproveSupplierProfileRequest(id, token);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Approvazione fallita");
    } finally {
      setProfileBusyId(null);
      setProfileBusyAction(null);
    }
  };

  const handleRejectProfileRequest = async (id) => {
    if (!window.confirm("Rifiutare questa modifica profilo fornitore?")) return;

    try {
      setProfileBusyId(id);
      setProfileBusyAction("reject");
      await adminRejectSupplierProfileRequest(id, token);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setProfileBusyId(null);
      setProfileBusyAction(null);
    }
  };

  const getLocationText = (f) => {
    return [f.indirizzo, f.cap, f.citta, f.provincia]
      .filter(Boolean)
      .join(" ");
  };

  const approveCrop = async () => {
    if (!crop || cropBusy) return;
    setCropBusy(true); setCropError("");
    try {
      if (cropTarget.type === "registration") await approveFornitore(cropTarget.id, token, crop, cropTarget.source);
      else await adminApproveSupplierProfileRequest(cropTarget.id, token, crop, cropTarget.source);
      setCropTarget(null); setCrop(null);
      toast.success("Foto ritagliata e approvata");
      await load();
    } catch (e) {
      setCropError(e.name === "TimeoutError" || e.name === "AbortError"
        ? "Tempo di attesa scaduto. Ricarica le richieste per verificare l'approvazione."
        : e.message || "Approvazione fallita. Riprova.");
    } finally { setCropBusy(false); }
  };

  return (
    <div className="admin-page approvals-page">
      {cropTarget && <div className="supplier-cropper-dialog" role="dialog" aria-modal="true" aria-label="Ritaglia e approva foto fornitore">
        <div className="supplier-cropper-panel">
          <h2>Ritaglia e approva foto</h2>
          <SupplierImageCropper source={cropTarget.source} disabled={cropBusy}
            onConfirm={setCrop} onChange={() => setCrop(null)} />
          {cropError && <p role="alert">{cropError}</p>}
          <div className="supplier-cropper-actions">
            <button type="button" disabled={cropBusy} onClick={() => { setCropTarget(null); setCrop(null); }}>Annulla</button>
            <button type="button" disabled={cropBusy || !crop} onClick={approveCrop}>
              {cropBusy ? "Generazione foto e approvazione…" : "Approva e salva foto"}
            </button>
          </div>
        </div>
      </div>}
      <div className="admin-overlay" />

      <main className="admin-content approvals-content">
        <section className="approvals-shell approvals-shell--narrow">
          <header className="approvals-pageHead">
            <div>
              <h1 className="approvals-pageTitle">Richieste fornitori</h1>
              <p className="approvals-pageSub">
                Gestisci registrazioni e modifiche profilo dei fornitori.
              </p>
            </div>
          </header>

          {/* =========================
              CARD 1 - NUOVI FORNITORI
             ========================= */}
          <section className="approvals-card">
            <div className="approvals-cardHead">
              <div>
                <h2 className="approvals-cardTitle">
                  Nuove richieste fornitori
                </h2>
                <p className="approvals-cardHint">
                  Registrazioni in attesa di approvazione.
                </p>
              </div>
            </div>

            <div className="approvals-cardBody">
              {loading ? (
                <p>Caricamento…</p>
              ) : items.length === 0 ? (
                <p>Nessuna richiesta in sospeso 🎉</p>
              ) : (
                <>
                  {/* ===== DESKTOP TABLE ===== */}
                  <div className="approvals-tableDesktop">
                    <div className="approvals-tableWrap approvals-tableWrap--compact">
                      <table className="approvals-table">
                        <thead>
                          <tr>
                            <th className="approvals-th">Richiedente</th>
                            <th className="approvals-th">Contatti</th>
                            <th className="approvals-th">Dati fiscali</th>
                            <th className="approvals-th">Località</th>
                            <th className="approvals-th approvals-thActions">
                              Azioni
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {items.map((f) => {
                            const isBusy = busyId === f.user_id;
                            const fullName = [f.name, f.last_name]
                              .filter(Boolean)
                              .join(" ")
                              .trim();

                            const activityName = f.nome_attivita || "";

                            return (
                              <tr
                                key={f.id}
                                className="approvals-row approvals-rowCompact"
                              >
                                <td className="approvals-td approvals-tdRequester">
                                  <div className="approvals-mainCell approvals-mainCellCompact">
                                    <div className="approvals-nameRow approvals-nameRowCompact">
                                      <span className="approvals-name approvals-nameStrong">
                                        {fullName || "Nome non disponibile"}
                                      </span>

                                      <span className="approvals-pendingBadge">
                                        In attesa
                                      </span>
                                    </div>

                                    <div className="approvals-subline approvals-sublineCompact">
                                      {activityName || "Attività non disponibile"}
                                    </div>
                                  </div>
                                </td>

                                <td className="approvals-td">
                                  <div className="approvals-contactBlock approvals-contactBlockCompact">
                                    {f.telefono && (
                                      <div className="approvals-inlineLine">
                                        <span className="approvals-inlineLabel">
                                          Tel
                                        </span>
                                        <span>{f.telefono}</span>
                                      </div>
                                    )}

                                    {f.email ? (
                                      <div className="approvals-inlineLine">
                                        <span className="approvals-inlineLabel">
                                          Email
                                        </span>
                                        <span className="approvals-contactEmail">
                                          {f.email}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="approvals-contactMuted">
                                        Email non disponibile
                                      </div>
                                    )}
                                  </div>
                                </td>

                                <td className="approvals-td">
                                  <div className="approvals-contactBlock approvals-contactBlockCompact">
                                    <div className="approvals-inlineLine">
                                      <span className="approvals-inlineLabel">
                                        CF
                                      </span>
                                      <span>
                                        {f.codice_fiscale || "Non disponibile"}
                                      </span>
                                    </div>

                                    <div className="approvals-inlineLine">
                                      <span className="approvals-inlineLabel">
                                        P.IVA
                                      </span>
                                      <span>
                                        {f.partita_iva || "Non disponibile"}
                                      </span>
                                    </div>
                                  </div>
                                </td>

                                <td className="approvals-td">
                                  <div className="approvals-locationBlock approvals-locationBlockCompact">
                                    <div className="approvals-locationMain">
                                      {getLocationText(f) ||
                                        "Indirizzo non disponibile"}
                                    </div>
                                  </div>
                                </td>

                                <td className="approvals-td approvals-tdActions">
                                  <div className="approvals-actions approvals-actionsCompact">
                                    <button
                                      type="button"
                                      className="approvals-btn approvals-btnPrimary"
                                      onClick={() => handleApprove(f.user_id)}
                                      disabled={isBusy}
                                    >
                                      {isBusy && busyAction === "approve"
                                        ? "Approvazione..."
                                        : "Approva"}
                                    </button>

                                    <button
                                      type="button"
                                      className="approvals-btn approvals-btnDanger"
                                      onClick={() => handleReject(f.user_id)}
                                      disabled={isBusy}
                                    >
                                      {isBusy && busyAction === "reject"
                                        ? "Rifiuto..."
                                        : "Rifiuta"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ===== MOBILE ===== */}
                  <div className="approvals-mobileList">
                    {items.map((f) => {
                      const isBusy = busyId === f.user_id;
                      const fullName = [f.name, f.last_name]
                        .filter(Boolean)
                        .join(" ")
                        .trim();

                      const activityName = f.nome_attivita || "";

                      return (
                        <article className="approvals-mobileCard" key={f.id}>
                          <div className="approvals-mobileTop">
                            <div className="approvals-mobileTitleWrap">
                              <div className="approvals-mobileMeta">
                                <span className="approvals-pendingBadge">
                                  In attesa
                                </span>
                              </div>

                              <h3 className="approvals-mobileTitle">
                                {fullName || "Nome non disponibile"}
                              </h3>

                              <p className="approvals-mobileSub">
                                {activityName || "Attività non disponibile"}
                              </p>
                            </div>
                          </div>

                          <div className="approvals-mobileGrid">
                            <div className="approvals-mobileStat">
                              <span className="approvals-mobileLabel">
                                Telefono
                              </span>
                              <span className="approvals-mobileValue">
                                {f.telefono || "Non disponibile"}
                              </span>
                            </div>

                            <div className="approvals-mobileStat">
                              <span className="approvals-mobileLabel">
                                Email
                              </span>
                              <span className="approvals-mobileValue approvals-break">
                                {f.email || "Non disponibile"}
                              </span>
                            </div>

                            <div className="approvals-mobileStat">
                              <span className="approvals-mobileLabel">
                                Codice fiscale
                              </span>
                              <span className="approvals-mobileValue approvals-break">
                                {f.codice_fiscale || "Non disponibile"}
                              </span>
                            </div>

                            <div className="approvals-mobileStat">
                              <span className="approvals-mobileLabel">
                                Partita IVA
                              </span>
                              <span className="approvals-mobileValue approvals-break">
                                {f.partita_iva || "Non disponibile"}
                              </span>
                            </div>

                            <div className="approvals-mobileStat approvals-mobileStatFull">
                              <span className="approvals-mobileLabel">
                                Indirizzo
                              </span>
                              <span className="approvals-mobileValue">
                                {getLocationText(f) ||
                                  "Indirizzo non disponibile"}
                              </span>
                            </div>
                          </div>

                          <div className="approvals-mobileActions">
                            <button
                              type="button"
                              className="approvals-btn approvals-btnPrimary"
                              onClick={() => handleApprove(f.user_id)}
                              disabled={isBusy}
                            >
                              {isBusy && busyAction === "approve"
                                ? "Approvazione..."
                                : "Approva"}
                            </button>

                            <button
                              type="button"
                              className="approvals-btn approvals-btnDanger"
                              onClick={() => handleReject(f.user_id)}
                              disabled={isBusy}
                            >
                              {isBusy && busyAction === "reject"
                                ? "Rifiuto..."
                                : "Rifiuta"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* =====================================
              CARD 2 - MODIFICHE PROFILO FORNITORE
             ===================================== */}
          <section className="approvals-card">
            <div className="approvals-cardHead">
              <div>
                <h2 className="approvals-cardTitle">
                  Modifiche profilo fornitori
                </h2>
                <p className="approvals-cardHint">
                  Approva o rifiuta richieste di alias e immagine profilo.
                </p>
              </div>
            </div>

            <div className="approvals-cardBody">
              {profileReqLoading ? (
                <p>Caricamento…</p>
              ) : profileRequests.length === 0 ? (
                <p>Nessuna richiesta modifica profilo in sospeso.</p>
              ) : (
                <div className="approvals-profileReqList">
                  {profileRequests.map((r) => {
                    const isBusy = profileBusyId === r.id;
                    const fullName = [r.name, r.last_name]
                      .filter(Boolean)
                      .join(" ")
                      .trim();

                    return (
                      <article className="approvals-profileReqItem" key={r.id}>
                        <div className="approvals-profileReqTop">
                          <div>
                            <h3 className="approvals-profileReqName">
                              {fullName || "Fornitore"}
                            </h3>
                            <p className="approvals-profileReqSub">
                              {r.email || "Email non disponibile"}
                            </p>
                          </div>

                          <span className="approvals-pendingBadge">
                            In attesa
                          </span>
                        </div>

                        <div className="approvals-profileReqGrid">
                          <div className="approvals-profileReqBox">
                            <span className="approvals-profileReqLabel">
                              Alias attuale
                            </span>
                            <span className="approvals-profileReqValue">
                              {r.current_nome || "—"}
                            </span>
                          </div>

                          <div className="approvals-profileReqBox">
                            <span className="approvals-profileReqLabel">
                              Alias richiesto
                            </span>
                            <span className="approvals-profileReqValue">
                              {r.requested_nome || "—"}
                            </span>
                          </div>

                          <div className="approvals-profileReqBox approvals-profileReqBox--full">
                            <span className="approvals-profileReqLabel">
                              Immagini
                            </span>

                            <div className="approvals-profileReqImages">
                              <div className="approvals-profileReqImageWrap">
                                <span className="approvals-profileReqImageLabel">
                                  Attuale
                                </span>
                                {r.current_cover_url ? (
                                  <img
                                    src={r.current_cover_url}
                                    alt="Immagine attuale"
                                    className="approvals-profileReqImage"
                                  />
                                ) : (
                                  <div className="approvals-profileReqValue">
                                    —
                                  </div>
                                )}
                              </div>

                              <div className="approvals-profileReqImageWrap">
                                <span className="approvals-profileReqImageLabel">
                                  Richiesta
                                </span>
                                {r.requested_cover_url ? (
                                  <img
                                    src={r.requested_cover_url}
                                    alt="Nuova immagine"
                                    className="approvals-profileReqImage"
                                  />
                                ) : (
                                  <div className="approvals-profileReqValue">
                                    —
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="approvals-profileReqActions">
                          <button
                            type="button"
                            className="approvals-btn approvals-btnPrimary"
                            onClick={() => handleApproveProfileRequest(r.id)}
                            disabled={isBusy}
                          >
                            {isBusy && profileBusyAction === "approve"
                              ? "Approvazione..."
                              : "Approva"}
                          </button>

                          <button
                            type="button"
                            className="approvals-btn approvals-btnDanger"
                            onClick={() => handleRejectProfileRequest(r.id)}
                            disabled={isBusy}
                          >
                            {isBusy && profileBusyAction === "reject"
                              ? "Rifiuto..."
                              : "Rifiuta"}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}
